/**
 * Settings > Case Preparation > Custom Case Types: Custom Workflows the firm
 * saved under a name ("Custom: EB-1A + Consular"). Everyone picks them on New
 * Case; owners and admins add, edit, duplicate and delete them. A deleted
 * type's cases keep its name and fall back to plain Custom Workflow defaults.
 */
import {
  Copy,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { ConfirmDeleteDialog } from "@/components/form";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "@/components/ui/tag";
import { draftTypeLabel } from "@/features/caseIde/workflows/defaultTemplates";
import { customCaseTypesApi } from "@/features/customCaseTypes/api";
import { CustomCaseTypeDialog } from "@/features/customCaseTypes/components/CustomCaseTypeDialog";
import { customTypeLabel } from "@/features/customCaseTypes/customCaseTypes";
import type { CustomCaseType } from "@/features/customCaseTypes/types";
import { useCustomCaseTypes } from "@/features/customCaseTypes/useCustomCaseTypes";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";

import { CasePrepRow, CasePrepSection } from "./casePrepParts";

export const CUSTOM_CASE_TYPES_ANCHOR = "case-prep-custom-types";

type Props = {
  lawFirmId: string;
  /** Scroll to the Drafting section, where letter templates are marked. */
  onJumpToDrafting?: () => void;
};

const lettersSummary = (type: CustomCaseType): string =>
  type.letters
    ? type.letters.length
      ? type.letters.map((l) => draftTypeLabel(l.draft_type)).join(", ")
      : "No letters"
    : type.base_visa
    ? `Letters of ${type.base_visa}`
    : "Custom Workflow's letters";

export const CustomCaseTypesSection: FC<Props> = ({
  lawFirmId,
  onJumpToDrafting
}) => {
  const { types, canManage, isLoading, error, refresh } =
    useCustomCaseTypes(lawFirmId);
  const [editing, setEditing] = useState<CustomCaseType | null | undefined>(
    undefined
  );
  const [deleting, setDeleting] = useState<CustomCaseType | null>(null);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState<FirmLibraryEntry[]>([]);

  useEffect(() => {
    if (editing === undefined) return undefined;
    let cancelled = false;
    firmKnowledgeApi
      .listLibrary({ lawFirmId }, "active")
      .then((data) => {
        if (!cancelled) setLibrary(data.entries);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [editing, lawFirmId]);

  const duplicate = async (type: CustomCaseType) => {
    setBusy(true);
    try {
      const copy = await customCaseTypesApi.duplicate(lawFirmId, type.id);
      toast.success(`Added ${customTypeLabel(copy.name)}`);
      await refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Couldn't duplicate the type."
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await customCaseTypesApi.remove(lawFirmId, deleting.id);
      toast.success(`Deleted ${customTypeLabel(deleting.name)}`);
      setDeleting(null);
      await refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Couldn't delete the type."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <CasePrepSection
      title="Custom Case Types"
      description="Custom Workflows saved under a name. Attorneys pick them on New Case as “Custom: <name>”, and their cases start with the type's letters and templates, or its base type's."
      testId="case-prep-custom-types"
    >
      {error ? (
        <div
          className="flex items-center justify-between gap-4 px-4 py-3 text-sm text-danger"
          data-testid="custom-types-error"
        >
          {error}
          <Button variant="secondary" size="sm" onClick={() => void refresh()}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-1/2 bg-fill-2" />
          <Skeleton className="h-4 w-1/3 bg-fill-2" />
        </div>
      ) : (
        <ul
          className="flex flex-col divide-y divide-line-1"
          data-testid="custom-types-list"
        >
          {types.length === 0 ? (
            <li>
              <EmptyState
                layout="inline"
                icon={Layers}
                title="No Custom Case Types Yet"
                description={
                  canManage
                    ? "Add one here, or check Save for the Firm when you name a Custom Workflow on New Case."
                    : "Firm admins add them here or on New Case."
                }
                data-testid="custom-types-empty"
              />
            </li>
          ) : null}
          {types.map((type) => (
            <li key={type.id} data-testid={`custom-type-${type.id}`}>
              <CasePrepRow
                title={
                  <span className="flex items-center gap-2">
                    {customTypeLabel(type.name)}
                    {type.base_visa ? (
                      <Tag>Based On {type.base_visa}</Tag>
                    ) : null}
                  </span>
                }
                description={lettersSummary(type)}
                control={
                  canManage ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          disabled={busy}
                          aria-label={`${customTypeLabel(type.name)} Actions`}
                          data-testid={`custom-type-actions-${type.id}`}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setEditing(type)}>
                          <Pencil />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void duplicate(type)}>
                          <Copy />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => setDeleting(type)}
                        >
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : undefined
                }
              />
            </li>
          ))}
          {canManage || onJumpToDrafting ? (
            <li className="flex items-center gap-2 px-4 py-2">
              {canManage ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditing(null)}
                  data-testid="custom-types-add"
                >
                  <Plus />
                  Add Custom Case Type
                </Button>
              ) : null}
              {onJumpToDrafting ? (
                <Button variant="ghost" size="sm" onClick={onJumpToDrafting}>
                  Drafting Templates
                </Button>
              ) : null}
            </li>
          ) : null}
        </ul>
      )}
      <CustomCaseTypeDialog
        lawFirmId={lawFirmId}
        editing={editing}
        library={library}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        onSaved={() => void refresh()}
      />
      <ConfirmDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting ? customTypeLabel(deleting.name) : ""}?`}
        description="New Case stops offering it. Its cases keep the name and use plain Custom Workflow defaults from now on."
        isPending={busy}
        onConfirm={() => void remove()}
      />
    </CasePrepSection>
  );
};
